import { Utxo } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  Heading,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  StackDivider,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/core';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { decodeRawTransaction } from '@liquality/bitcoin-utils';
import BigNumber from 'bignumber.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BtcImage from '../assets/btc.svg';
import config from '../config/config';
import { getClient } from '../services/chainClient';
import { RootState } from '../store/reducers';
import { sleep } from '../utils/Async';
import LineItem from './LineItem';
const {
  setIntervalAsync,
  clearIntervalAsync,
} = require('set-interval-async/dynamic');

const EnterAddress = ({
  btcAddress,
  setBtcAddress,
  onWithdraw,
  isWithdrawing,
}) => (
  <Box textAlign="center">
    <Image src={BtcImage} mx="auto" height="75px" mb={1} />
    <Heading size="md" mb={2}>
      Please enter the BTC receiving address
    </Heading>
    <Input
      width="200px"
      placeholder="Enter your BTC address"
      value={btcAddress}
      onChange={(evt) => setBtcAddress(evt.target.value)}
    ></Input>
    <Flex mt="2rem">
      <Button
        ml="auto"
        backgroundColor="blue.600"
        isLoading={isWithdrawing}
        loadingText="Withdrawing..."
        color="white"
        onClick={onWithdraw}
      >
        Withdraw Winnings
      </Button>
    </Flex>
  </Box>
);

const OverviewPage = ({
  betSize,
  setPage,
  totalTransferredBTC,
  feesBTC,
  sweepFeeBTC,
  totalPayoutWinningBTC,
}) => (
  <Box>
    <Heading mb={4}>Congratulations!</Heading>

    <VStack
      divider={<StackDivider borderColor="gray.400" />}
      spacing={4}
      align="stretch"
    >
      <VStack spacing={4} align="stretch">
        <LineItem label="Total Payout Winnings">
          {totalPayoutWinningBTC} BTC
        </LineItem>
        <LineItem label="Our 5% fees">{feesBTC} BTC</LineItem>
        <LineItem label="BTC Network Fees">{sweepFeeBTC} BTC</LineItem>
      </VStack>
      <VStack spacing={4} align="stretch">
        <LineItem label="Total Transferred">{totalTransferredBTC} BTC</LineItem>
      </VStack>
    </VStack>

    <Flex mt="2rem">
      <Button
        ml="auto"
        backgroundColor="blue.600"
        color="white"
        rightIcon={<ChevronRightIcon />}
        onClick={() => setPage(1)}
      >
        Withdraw Winnings
      </Button>
    </Flex>
  </Box>
);

const WithdrawingPage = () => (
  <Box textAlign="center">
    <CircularProgress
      // @ts-ignore
      isIndeterminate
      thickness="3px"
      size="80px"
      color="blue.600"
      mb={4}
    />
    <Heading mb={4} size="md">
      Transferring your winnings...
    </Heading>
  </Box>
);

const CompletePage = ({ txHash }) => {
  const history = useHistory();
  return (
    <Box textAlign="center">
      <Heading mb={4} size="md">
        Transfer Complete!
      </Heading>

      <Heading as="h2" size="sm" mb={8} mt={8}>
        View Explorer:{' '}
        <Link
          textDecoration="underline"
          fontWeight="bold"
          href={`${config.bitcoin.explorer}/${txHash}`}
          target="_blank"
        >
          {txHash.substring(0, 15)}...
        </Link>
      </Heading>
      <Flex mt="2rem">
        <Button
          ml="auto"
          backgroundColor="blue.600"
          color="white"
          onClick={() => history.push('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </Flex>
    </Box>
  );
};

const WithdrawWinningsModal = ({ contractId, isOpen, onClose }) => {
  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);
  const bet = useSelector((state: RootState) => state.bets[contractId]);
  const { betSize } = bet.betDetails;

  const [page, setPage] = useState(0);
  const [btcAddress, setBtcAddress] = useState('');
  const [withdrawTxHash, setWithdrawTxHash] = useState('');

  const [totalTransferredBTC, setTotalTransferredBTC] = useState(0);
  const [feesBTC, setFeesBTC] = useState(0);
  const [sweepFeeBTC, setSweepFeeBTC] = useState(0);
  const [totalPayoutWinningBTC, setTotalPayoutWinningBTC] = useState(0);

  const [isWithdrawing, setIsWithdrawing] = useState(false);

  useEffect(() => {
    if (!mnemonic) return;
    (async () => {
      const bitcoin = getClient();
      const betClose = bet.closeInfo!;
      const { signature, outcomeIndex } = betClose;

      const contract = bet.contract;

      const addresses = await bitcoin.getMethod('getAddresses')(
        contract.startingIndex,
        2
      );

      const changeAddresses = await bitcoin.getMethod('getAddresses')(
        contract.startingIndex,
        2,
        true
      );

      const [
        ,
        closingTxHex,
      ] = await bitcoin.client.finance.dlc.buildUnilateralClose(
        signature,
        outcomeIndex,
        contractId
      );

      const network = await bitcoin.client.getMethod('getConnectedNetwork')();
      const closingTx = decodeRawTransaction(closingTxHex, network);

      setTotalPayoutWinningBTC(closingTx.vout[0].value);

      let utxos: Utxo[] = [];
      closingTx.vout.forEach((vout) => {
        const { n, scriptPubKey, value } = vout;
        utxos.push({
          txid: closingTx.txid,
          vout: n,
          amount: value,
          address: scriptPubKey.addresses[0],
        });
      });

      const addrList = addresses.concat(changeAddresses);

      let fixedInputs = utxos.map((utxo) => {
        const addr = addrList.find((a) => a.equals(utxo.address));
        console.log('addr', addr);
        console.log('utxo', utxo);
        return {
          ...utxo,
          value: new BigNumber(utxo.amount).times(1e8).toNumber(),
          derivationPath: addr.derivationPath,
        };
      });
      fixedInputs = fixedInputs.filter(
        (v, i, a) => a.findIndex((t) => t.txid === v.txid) === i
      );

      const outputs = [
        {
          to: addresses[0].address,
          value: parseInt(
            new BigNumber(fixedInputs[0].value).times(config.fee).toFixed()
          ),
        },
      ];
      const {
        fee: sweepFee,
        hex: sweepTxHex,
      } = await bitcoin.client.finance.wallet.buildSweepTransactionWithSetOutputs(
        addresses[1].address,
        false,
        outputs,
        fixedInputs
      );
      const sweepTx = decodeRawTransaction(sweepTxHex, network);
      const transferredIndex = sweepTx.vout[0].value > sweepTx.vout[1].value;

      setTotalTransferredBTC(sweepTx.vout[transferredIndex ? 0 : 1].value);
      setFeesBTC(sweepTx.vout[transferredIndex ? 1 : 0].value);
      setSweepFeeBTC(
        parseFloat(new BigNumber(sweepFee).dividedBy(1e8).toFixed(8))
      );
    })();
  }, [totalTransferredBTC, feesBTC, sweepFeeBTC, totalPayoutWinningBTC, bet.closeInfo, bet.contract, contractId, isOpen, mnemonic]);

  const onWithdraw = async () => {
    console.log('withdrawWinnings');
    setIsWithdrawing(true);
    try {
      const bitcoin = getClient();
      const betClose = bet.closeInfo!;
      const { signature, outcomeIndex } = betClose;
      setPage(2);

      const contract = bet.contract;

      const addresses = await bitcoin.getMethod('getAddresses')(
        contract.startingIndex,
        2
      );

      const changeAddresses = await bitcoin.getMethod('getAddresses')(
        contract.startingIndex,
        2,
        true
      );

      const oldBalance = await bitcoin.getMethod('getBalance')([
        ...addresses,
        ...changeAddresses,
      ]);

      console.log(oldBalance);

      let txHashes;
      if (
        !(
          betClose.path === 'localToRemote' || betClose.path === 'remoteToLocal'
        )
      ) {
        txHashes = await bitcoin.client.finance.dlc.unilateralClose(
          signature,
          outcomeIndex,
          contractId
        );
        console.log('txHashes', txHashes);
        console.log('waiting for tx to propogate...');
      }

      let balance;
      const directWithdrawPaths = [
        'localToLocal',
        'localToRemote',
        'remoteToLocal',
        'remoteToRemote',
      ];
      if (!directWithdrawPaths.includes(betClose.path)) {
        await new Promise((resolve) => {
          const checkInterval = setIntervalAsync(async () => {
            balance = await bitcoin.getMethod('getBalance')([
              ...addresses,
              ...changeAddresses,
            ]);

            let tx;
            try {
              tx = await bitcoin.getMethod('getTransactionByHash')(txHashes[1]);
              console.log('new tx', tx);
            } catch (e) {
              console.log(`tx doesn't exist`);
            }

            if (balance > oldBalance || tx) {
              clearIntervalAsync(checkInterval);
              resolve();
            }
          }, 5000);
        });
      }

      let utxos: Utxo[] = [];
      while (utxos.length === 0) {
        await sleep(1000);

        utxos = await bitcoin.getMethod('getUnspentTransactions')([
          contract.isLocalParty
            ? contract.localPartyInputs.finalAddress
            : contract.remotePartyInputs.finalAddress,
        ]);
      }
      const addrList = addresses.concat(changeAddresses);

      let fixedInputs = utxos.map((utxo) => {
        const addr = addrList.find((a) => a.equals(utxo.address));
        console.log('addr', addr);
        console.log('utxo', utxo);
        return {
          ...utxo,
          value: new BigNumber(utxo.amount).times(1e8).toNumber(),
          derivationPath: addr.derivationPath,
        };
      });
      console.log('fixedInputs', fixedInputs);

      const { address: payAddress } = await (
        await fetch(`${config.pay.url}/address`)
      ).json();

      const outputs = [
        {
          to: payAddress,
          value: parseInt(
            new BigNumber(fixedInputs[0].value).times(config.fee).toFixed()
          ),
        },
      ];
      console.log('outputs', outputs);

      const txHash = await bitcoin.client.finance.wallet.sendSweepTransactionWithSetOutputs(
        btcAddress,
        false,
        outputs,
        fixedInputs
      );
      console.log('txHash', txHash);

      setWithdrawTxHash(txHash.hash);
      setPage(3);
    } catch (e) {
      throw e;
    } finally {
      setIsWithdrawing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay>
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={4}>
            <Tabs isLazy index={page}>
              <TabPanels>
                <TabPanel>
                  <OverviewPage
                    betSize={betSize}
                    setPage={setPage}
                    totalTransferredBTC={totalTransferredBTC}
                    feesBTC={feesBTC}
                    sweepFeeBTC={sweepFeeBTC}
                    totalPayoutWinningBTC={totalPayoutWinningBTC}
                  />
                </TabPanel>
                <TabPanel>
                  <EnterAddress
                    setBtcAddress={setBtcAddress}
                    btcAddress={btcAddress}
                    onWithdraw={onWithdraw}
                    isWithdrawing={isWithdrawing}
                  />
                </TabPanel>
                <TabPanel>
                  <WithdrawingPage />
                </TabPanel>
                <TabPanel>
                  <CompletePage txHash={withdrawTxHash} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default WithdrawWinningsModal;
